import { useMemo } from 'react'

import {
  CreateOrganizationHolidayMutation,
  CreateOrganizationHolidayMutationVariables,
  DeleteOrganizationHolidayMutation,
  DeleteOrganizationHolidayMutationVariables,
  GetOrganizationHolidaysQuery,
  GetOrganizationHolidaysQueryVariables,
  IsDayAHolidayForOrganizationQuery,
  IsDayAHolidayForOrganizationQueryVariables,
  UpdateOrganizationHolidayMutation,
  UpdateOrganizationHolidayMutationVariables,
} from 'types/graphql'

import { QueryKey, useMutation, useQuery } from '../utils/queryClient'

const GetOrganizationHolidays = gql`
  query GetOrganizationHolidaysQuery($orgSlug: String!, $year: Int!) {
    organizationHolidays(orgSlug: $orgSlug, year: $year) {
      ...OrganizationHolidayDetailFragment
    }
  }
`

export const useOrganizationHolidaysQuery = (orgSlug: string, year: number) => {
  const res = useQuery<GetOrganizationHolidaysQuery, GetOrganizationHolidaysQueryVariables>({
    queryKey: [QueryKey.OrganizationHolidays, orgSlug, year],
    queryDocument: GetOrganizationHolidays,
    variables: { orgSlug, year },
  })
  const holidays = useMemo(() => res.data?.organizationHolidays ?? [], [res.data])
  return { ...res, holidays }
}

const Create = gql`
  mutation CreateOrganizationHolidayMutation($input: HolidayForOrganizationInput!) {
    createOrganizationHoliday(input: $input) {
      id
    }
  }
`

export const useCreateOrganizationHolidayMutation = () => {
  return useMutation<CreateOrganizationHolidayMutation, CreateOrganizationHolidayMutationVariables>(
    {
      mutationDocument: Create,
    }
  )
}

const Update = gql`
  mutation UpdateOrganizationHolidayMutation($id: Int!, $input: HolidayForOrganizationInput!) {
    updateOrganizationHoliday(id: $id, input: $input) {
      id
    }
  }
`

export const useUpdateOrganizationHolidayMutation = () => {
  return useMutation<UpdateOrganizationHolidayMutation, UpdateOrganizationHolidayMutationVariables>(
    {
      mutationDocument: Update,
    }
  )
}

const Delete = gql`
  mutation DeleteOrganizationHolidayMutation($id: Int!, $orgSlug: String!) {
    deleteOrganizationHoliday(id: $id, orgSlug: $orgSlug) {
      id
    }
  }
`

export const useDeleteOrganizationHolidayMutation = () => {
  return useMutation<DeleteOrganizationHolidayMutation, DeleteOrganizationHolidayMutationVariables>(
    {
      mutationDocument: Delete,
    }
  )
}

const IsDayHoliday = gql`
  query IsDayAHolidayForOrganizationQuery($organizationId: Int!, $day: DateTime!) {
    isDayAHolidayForOrganization(organizationId: $organizationId, day: $day) {
      ...OrganizationHolidayDetailFragment
    }
  }
`

export const useOrganizationHolidayForDayQuery = (args: {
  organizationId: number | null | undefined
  day: string | null | undefined
  enabled?: boolean
}) => {
  const { organizationId, day, enabled = true } = args

  const shouldFetch = enabled && !!organizationId && !!day

  const res = useQuery<
    IsDayAHolidayForOrganizationQuery,
    IsDayAHolidayForOrganizationQueryVariables
  >({
    queryKey: [QueryKey.OrganizationHolidayForDay, organizationId, day],
    queryDocument: IsDayHoliday,
    variables: {
      organizationId: organizationId ?? 0,
      day: day ?? new Date().toISOString(),
    },
    enabled: shouldFetch,
  })

  return {
    ...res,
    holiday: res.data?.isDayAHolidayForOrganization ?? null,
  }
}
